<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import SplitButton from 'primevue/splitbutton';
import type { MenuItem } from 'primevue/menuitem';
import BaseIcon from '@/components/base/BaseIcon.vue';
import KyxParecerPdfButton from '@/components/kyc-parecer-pdf/KyxParecerPdfButton.vue';
import { OUTCOME_LABELS } from '@/composables/useKycDecisionDraft';
import { getCurrentParecerDecision, getParecerAutomation, getParecerOutcomeTheme, type EntityParecerStatus } from '@/composables/parecerStatus';
import { formatDate } from '@/utils/formatters';

const props = withDefaults(defineProps<{
  status: EntityParecerStatus;
  isGeneratingReport: boolean;
  parecerMenuItems: MenuItem[];
  canEdit: boolean;
  canStartParecer?: boolean;
  startParecerBlockedReason?: string | null;
  workflowMissing: boolean;
  requiredRoleNames: string[];
}>(), {
  canEdit: true,
  canStartParecer: true,
  startParecerBlockedReason: null,
  workflowMissing: false,
  requiredRoleNames: () => [],
});

const emit = defineEmits<{
  'open-current-parecer': [];
  'open-latest-finalized-parecer': [];
  'open-decision-wizard': [requestId?: string];
  'take-parecer': [parecerId: string];
}>();

// Single source for the permission copy: the inline text and the tooltip both read it, so they
// cannot drift. Naming the roles is only possible when the stage actually grants edit to some
// role — otherwise no role can create a parecer here, which is what the user needs to be told.
const permissionMessage = computed(() => {
  const roles = props.requiredRoleNames;
  if (!roles.length) return 'Seu perfil não possui permissão para criar pareceres.';
  return `Requer papel: ${roles.join(', ')}`;
});

const permissionTooltip = computed(() => {
  if (props.canEdit || props.workflowMissing) return undefined;
  return permissionMessage.value;
});

// Precedence when several things block "Criar parecer" at once, highest first:
//   1. a parecer already exists for the report  (never reaches here: the card renders the
//      parecer's own state instead of this block, so it only disables the button)
//   2. the user lacks permission
//   3. the report/policy itself is unusable (deleted policy, no relationship types, stale report)
// Permission outranks (3) because it is user-scoped and survives switching policy: showing a
// policy-scoped reason first would just move the wall when the user picks another policy.
// Mirrors the order the block's inline text already uses.
const startParecerTooltip = computed(
  () => permissionTooltip.value ?? props.startParecerBlockedReason ?? undefined,
);

const isStartParecerDisabled = computed(() =>
  props.isGeneratingReport || !props.canEdit || !props.canStartParecer,
);

const currentParecer = computed(() => props.status.currentParecer);
const latestFinalizedParecer = computed(() => {
  const finalizedParecer = props.status.latestFinalizedParecer;
  if (!finalizedParecer) return null;
  if (finalizedParecer.parecerId === currentParecer.value?.parecerId) return null;
  return finalizedParecer;
});

const currentDecision = computed(() => getCurrentParecerDecision(currentParecer.value));

// The parecer is in the workflow's unassigned queue: it exists but nobody owns it. It must NOT
// look like a human who started and walked away — it's an unattended item that needs action.
const isQueued = computed(() => currentParecer.value?.currentStage.slug === 'pending');

// The common case for a queued parecer: the decision policy evaluated the entity, could neither
// approve nor reject it automatically, and opened it for a human. Worth saying so explicitly,
// since it explains why nobody is on it.
const isAutoManualQueued = computed(() =>
  isQueued.value
  && currentParecer.value?.createdBy === 'automation'
  && currentParecer.value?.policyDecision === 'manual',
);

// Taking a queued parecer is a real workflow transition, so the engine decides who may do it
// (the pending -> under_analysis transition's required_roles). The backend hands us both the
// verdict and a pt-BR reason; a missing transition means there is no way out of the queue at all.
const takeTransition = computed(() => currentParecer.value?.takeTransition ?? null);
const canTakeParecer = computed(() => takeTransition.value?.allowed === true);
const takeBlockedReason = computed(() => {
  if (canTakeParecer.value) return undefined;
  return takeTransition.value?.blockedReason
    ?? 'Seu perfil não tem permissão para iniciar este parecer.';
});

type RiskPill = { label: string; dot: string; bg: string; border: string; text: string };
const RISK_PILLS: Record<string, RiskPill> = {
  no_risk: { label: 'Sem risco', dot: '#639922', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800' },
  very_low: { label: 'Muito baixo', dot: '#639922', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800' },
  low: { label: 'Baixo', dot: '#639922', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800' },
  medium: { label: 'Médio', dot: '#BA7517', bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800' },
  high: { label: 'Alto', dot: '#D85A30', bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-800' },
  very_high: { label: 'Muito alto', dot: '#E24B4A', bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-800' },
};

const queuedRiskPill = computed<RiskPill | null>(() => {
  const slug = currentParecer.value?.reportRiskLevel;
  return slug ? RISK_PILLS[slug] ?? null : null;
});

// "esta empresa" / "esta pessoa" — both feminine, so "aprová-la nem reprová-la" fits either.
const queuedSubject = computed(() =>
  currentParecer.value?.entityType === 'person' ? 'esta pessoa' : 'esta empresa',
);

const queuedTitle = computed(() =>
  isAutoManualQueued.value ? 'Requer decisão manual' : 'Parecer pendente',
);

const queuedDescription = computed(() =>
  isAutoManualQueued.value
    ? `Um parecer foi aberto para ${queuedSubject.value}, mas a política de decisão não pôde `
      + 'aprová-la nem reprová-la automaticamente. É necessária uma decisão manual.'
    : `Um parecer foi aberto para ${queuedSubject.value} e ainda não tem responsável. `
      + 'Assuma o parecer para iniciar a análise.',
);

const queuedBadgeLabel = computed(() =>
  isAutoManualQueued.value ? 'Aberto pela automação' : 'Sem responsável',
);

const queuedBadgeIcon = computed(() => (isAutoManualQueued.value ? 'smart_toy' : 'pending_actions'));

const queuedOpenedAt = computed(() => formatDate(currentParecer.value?.openedAt, undefined, '—'));

// Once the parecer leaves the unattended waiting state (a human picked it up and it moved
// on to manager review / finalized), we keep the normal stage card but still surface how the
// automation was involved — quietly, not as a call to action.
const autoOriginInfo = computed(() =>
  getParecerAutomation(
    currentParecer.value?.createdBy,
    currentParecer.value?.policyDecision,
    currentParecer.value?.currentStage.slug === 'finalized',
  ),
);
// The chip only distinguishes "the policy decided it" from "the policy merely opened it";
// the strong "Requer decisão manual" treatment is reserved for the unattended card above.
const autoOriginChipLabel = computed(() =>
  autoOriginInfo.value?.kind === 'auto_finalized' ? 'Decisão automática' : 'Aberto pela automação',
);
const autoOriginChipTooltip = computed(() =>
  autoOriginInfo.value?.kind === 'auto_finalized'
    ? 'Decisão tomada automaticamente pela política de decisão.'
    : 'Este parecer foi aberto automaticamente pela política de decisão.',
);

// Colored pill for the decision's risk level (same palette as the auto-manual card).
const currentRiskPill = computed<RiskPill | null>(() => {
  const slug = currentDecision.value?.riskLevel;
  return slug ? RISK_PILLS[slug] ?? null : null;
});

const currentTheme = computed(() => {
  switch (currentParecer.value?.currentStage.slug) {
    case 'manager_review':
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-800',
        icon: 'hourglass_top',
        iconColor: 'text-amber-600',
        pillBg: 'bg-amber-100',
      };
    case 'request_for_information':
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: 'help_outline',
        iconColor: 'text-blue-600',
        pillBg: 'bg-blue-100',
      };
    default:
      if (currentDecision.value) return getParecerOutcomeTheme(currentDecision.value.decision);

      return {
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        text: 'text-slate-800',
        icon: 'edit_note',
        iconColor: 'text-slate-600',
        pillBg: 'bg-slate-100',
      };
  }
});

const latestFinalizedTheme = computed(() => {
  const finalizedDecision = latestFinalizedParecer.value?.finalDecision;
  return finalizedDecision ? getParecerOutcomeTheme(finalizedDecision.decision) : null;
});

const currentTitle = computed(() => {
  const parecer = currentParecer.value;
  if (!parecer) return '';

  switch (parecer.currentStage.slug) {
    case 'finalized':
      return 'Decisão final emitida';
    case 'manager_review':
      return 'Aguardando revisão gerencial';
    case 'request_for_information':
      return 'Aguardando informações adicionais';
    case 'under_analysis':
      return parecer.activeLane === 'final'
        ? 'Decisão final em elaboração'
        : 'Proposta de decisão em andamento';
    default:
      return 'Parecer';
  }
});

const currentDescription = computed(() => {
  const parecer = currentParecer.value;
  if (!parecer) return '';

  switch (parecer.currentStage.slug) {
    case 'finalized':
      return 'Este parecer já recebeu a decisão final registrada no workflow.';
    case 'manager_review':
      return 'A proposta do analista já foi enviada e o parecer segue pendente da decisão do gerente.';
    case 'request_for_information':
      return 'O parecer ficará pausado até o recebimento das informações solicitadas.';
    case 'under_analysis':
      return parecer.activeLane === 'final'
        ? 'Este workflow registra a decisão final diretamente nesta etapa.'
        : 'A proposta ainda está sendo preparada nesta etapa.';
    default:
      return 'Existe um parecer registrado para esta entidade.';
  }
});

const currentActionLabel = computed(() => {
  switch (currentParecer.value?.currentStage.slug) {
    case 'finalized':
      return 'Ver parecer';
    case 'manager_review':
      return 'Abrir revisão';
    case 'request_for_information':
      return 'Ver parecer';
    default:
      return 'Continuar';
  }
});

const currentActionIcon = computed(() => {
  switch (currentParecer.value?.currentStage.slug) {
    case 'finalized':
      return 'visibility';
    case 'manager_review':
      return 'gavel';
    case 'request_for_information':
      return 'visibility';
    default:
      return 'arrow_forward';
  }
});

const hasFinalizedCurrentParecer = computed(() => currentParecer.value?.currentStage.slug === 'finalized');

const currentDecisionBlockLabel = computed(() => {
  switch (currentParecer.value?.currentStage.slug) {
    case 'finalized':
      return 'Decisão final';
    case 'manager_review':
      return 'Proposta do analista';
    default:
      return 'Prévia da decisão';
  }
});

const currentDecisionLabel = computed(() => {
  if (!currentDecision.value) return '';
  return OUTCOME_LABELS[currentDecision.value.decision] ?? currentDecision.value.decision;
});

const currentActorLabel = computed(() =>
  currentParecer.value?.currentStage.slug === 'manager_review' ? 'Proponente' : 'Responsável',
);

const currentActorName = computed(() => currentDecision.value?.decidedByName || '—');

const currentDateValue = computed(() =>
  formatDate(currentDecision.value?.decidedAt || currentParecer.value?.updatedAt, undefined, '—'),
);

const currentRiskMetaLabel = computed(() =>
  currentParecer.value?.currentStage.slug === 'manager_review' ? 'Risco proposto' : 'Risco',
);

const handleCurrentAction = () => {
  const parecer = currentParecer.value;
  if (!parecer) return;

  // Queued: the parecer has to leave the queue before it can be worked, and that move is a
  // permission-gated transition — so the host performs it, then opens the wizard.
  if (isQueued.value) {
    if (!canTakeParecer.value) return;
    emit('take-parecer', parecer.parecerId);
    return;
  }

  if (parecer.currentStage.slug === 'under_analysis') {
    emit('open-decision-wizard', parecer.requestId);
    return;
  }

  emit('open-current-parecer');
};

const handleStartParecer = () => {
  if (isStartParecerDisabled.value) return;
  emit('open-decision-wizard');
};
</script>

<template>
  <section class="h-full min-h-[156px] rounded-xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col">
    <div class="text-xs font-semibold text-slate-500 uppercase tracking-[0.14em]">Status do Parecer</div>

    <div v-if="currentParecer" class="mt-2 flex-1 flex flex-col gap-2">
      <div
        v-if="isQueued"
        class="flex flex-1 overflow-hidden rounded-xl border border-amber-300 shadow-sm"
      >
        <div class="flex w-24 shrink-0 flex-col items-center justify-center gap-2 bg-amber-600 px-2 py-4 text-center text-amber-50">
          <BaseIcon class="text-3xl">{{ queuedBadgeIcon }}</BaseIcon>
          <span class="text-[10px] font-medium leading-tight">{{ queuedBadgeLabel }}</span>
        </div>

        <div class="flex flex-1 flex-col justify-center bg-amber-50 px-4 py-3.5">
          <div class="flex items-start justify-between gap-2.5">
            <div class="text-lg font-semibold leading-tight text-amber-900">
              {{ queuedTitle }}
            </div>
            <span
              v-if="queuedRiskPill"
              class="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium"
              :class="[queuedRiskPill.bg, queuedRiskPill.border, queuedRiskPill.text]"
            >
              <span class="h-1.5 w-1.5 rounded-full" :style="{ backgroundColor: queuedRiskPill.dot }"></span>
              Risco {{ queuedRiskPill.label.toLowerCase() }}
            </span>
          </div>

          <div class="mt-1.5 text-[13px] leading-relaxed text-amber-800">
            {{ queuedDescription }}
          </div>

          <div class="mt-3.5 flex items-center justify-between gap-3">
            <span class="inline-flex items-center gap-1.5 text-[11px] text-amber-700">
              <BaseIcon class="text-sm">calendar_today</BaseIcon>
              {{ queuedOpenedAt }} · sem responsável
            </span>

            <!-- Span wrapper so the tooltip still shows while the button is disabled -->
            <span v-tooltip.top="takeBlockedReason">
              <Button
                label="Assumir e decidir"
                size="small"
                :disabled="!canTakeParecer"
                class="!h-8 !border-amber-700 !bg-amber-600 !text-xs !text-white hover:!bg-amber-700"
                @click="handleCurrentAction"
              >
                <template #icon>
                  <BaseIcon class="text-sm">gavel</BaseIcon>
                </template>
              </Button>
            </span>
          </div>
        </div>
      </div>

      <template v-else>
      <div class="rounded-xl border p-3 shadow-sm" :class="[currentTheme.border, currentTheme.bg]">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80 ring-1 ring-inset ring-black/5">
            <BaseIcon :class="['text-xl', currentTheme.iconColor]">{{ currentTheme.icon }}</BaseIcon>
          </div>

          <div :class="['min-w-0 flex-1 text-lg font-semibold leading-tight', currentTheme.text]">
            {{ currentTitle }}
          </div>

          <div class="flex shrink-0 items-center gap-1.5">
            <span
              v-if="autoOriginInfo"
              v-tooltip.top="autoOriginChipTooltip"
              class="inline-flex items-center gap-1 rounded-full border border-white/80 bg-white/75 px-2 py-0.5 text-[11px] font-medium text-slate-500"
            >
              <BaseIcon class="text-xs">smart_toy</BaseIcon>
              {{ autoOriginChipLabel }}
            </span>
            <span
              v-if="currentDecision?.isExpired"
              class="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800"
            >
              Expirado
            </span>
          </div>
        </div>

        <p class="mt-2 text-xs leading-5 text-slate-600">
          {{ currentDescription }}
        </p>

        <dl class="mt-3 grid grid-cols-[max-content_1fr] items-baseline gap-x-6 gap-y-2.5 border-t border-slate-900/5 pt-3 text-sm">
          <template v-if="currentDecision">
            <dt class="whitespace-nowrap font-medium text-slate-600">
              {{ currentDecisionBlockLabel }}
            </dt>
            <dd class="flex items-center gap-1.5">
              <BaseIcon :class="['text-base', getParecerOutcomeTheme(currentDecision.decision).iconColor]">
                {{ getParecerOutcomeTheme(currentDecision.decision).icon }}
              </BaseIcon>
              <span :class="['font-semibold', getParecerOutcomeTheme(currentDecision.decision).text]">
                {{ currentDecisionLabel }}
              </span>
            </dd>
          </template>

          <dt class="whitespace-nowrap font-medium text-slate-600">
            {{ currentRiskMetaLabel }}
          </dt>
          <dd>
            <span
              v-if="currentRiskPill"
              class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium"
              :class="[currentRiskPill.bg, currentRiskPill.border, currentRiskPill.text]"
            >
              <span class="h-1.5 w-1.5 rounded-full" :style="{ backgroundColor: currentRiskPill.dot }"></span>
              {{ currentRiskPill.label }}
            </span>
            <span v-else class="text-slate-600">Mantido do relatório</span>
          </dd>

          <template v-if="currentDecision?.relationshipTypes.length">
            <dt class="whitespace-nowrap font-medium text-slate-600">
              Relacionamentos
            </dt>
            <dd class="flex flex-wrap items-center gap-x-1.5 font-medium capitalize text-slate-800">
              <template v-for="(relationshipType, index) in currentDecision.relationshipTypes" :key="relationshipType">
                <span>{{ relationshipType }}</span>
                <span v-if="index < currentDecision.relationshipTypes.length - 1" class="text-slate-400">·</span>
              </template>
            </dd>
          </template>
          <template v-else-if="currentDecision?.decision === 'rejected'">
            <dt class="whitespace-nowrap font-medium text-slate-600">
              Relacionamentos
            </dt>
            <dd class="text-slate-600">Entidade não aprovada.</dd>
          </template>

          <dt class="whitespace-nowrap font-medium text-slate-600">
            {{ currentActorLabel }}
          </dt>
          <dd class="font-medium text-slate-800">
            {{ currentActorName }}<span class="font-normal text-slate-500"> · {{ currentDateValue }}</span>
          </dd>
        </dl>

        <div class="mt-3 flex justify-end gap-2 border-t border-slate-900/5 pt-3">
          <KyxParecerPdfButton
            :parecer-id="currentParecer.parecerId"
            :is-finalized="hasFinalizedCurrentParecer"
          />

          <Button
            :label="currentActionLabel"
            class="!h-9 !gap-1.5 !px-4 !text-sm !font-medium"
            @click="handleCurrentAction"
          >
            <template #icon>
              <BaseIcon class="text-base">{{ currentActionIcon }}</BaseIcon>
            </template>
          </Button>
        </div>
      </div>
      </template>
    </div>

    <div v-if="latestFinalizedParecer" class="mt-3">
      <div class="rounded-xl border p-3 shadow-sm" :class="[latestFinalizedTheme?.border, latestFinalizedTheme?.bg]">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Último parecer finalizado
            </div>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <BaseIcon :class="['text-xl', latestFinalizedTheme?.iconColor]">
                {{ latestFinalizedTheme?.icon }}
              </BaseIcon>
              <span :class="['text-lg leading-tight font-semibold', latestFinalizedTheme?.text]">
                {{
                  latestFinalizedParecer.finalDecision?.decision === 'approved_with_conditions'
                    ? 'Aprovado com Condições'
                    : latestFinalizedParecer.finalDecision?.decision === 'approved'
                      ? 'Aprovado'
                      : 'Reprovado'
                }}
              </span>
              <span class="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
                {{ latestFinalizedParecer.currentStage.name }}
              </span>
            </div>

            <div class="mt-0.5 text-xs text-slate-500">
              Decisão final anterior que ainda existe no histórico da entidade.
            </div>

            <div class="mt-2 text-xs text-slate-500 flex flex-wrap items-center gap-2">
              <span v-if="latestFinalizedParecer.finalDecision?.decidedByName">Por {{ latestFinalizedParecer.finalDecision.decidedByName }}</span>
              <span v-if="latestFinalizedParecer.finalDecision?.decidedByName">•</span>
              <span>{{ formatDate(latestFinalizedParecer.finalDecision?.decidedAt || latestFinalizedParecer.updatedAt) }}</span>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-1">
            <KyxParecerPdfButton
              :parecer-id="latestFinalizedParecer.parecerId"
              :is-finalized="true"
            />
            <Button
              text
              rounded
              size="small"
              severity="primary"
              aria-label="Ver último parecer finalizado"
              @click="emit('open-latest-finalized-parecer')"
            >
              <BaseIcon class="text-lg">visibility</BaseIcon>
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!currentParecer" class="mt-2 flex-1 flex flex-col justify-between">
      <div
        :class="[
          'w-full rounded-lg border px-3 py-2.5',
          workflowMissing ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-slate-50/70',
        ]"
      >
        <div class="flex items-start gap-2">
          <BaseIcon :class="['text-base mt-0.5 shrink-0', workflowMissing ? 'text-amber-600' : 'text-slate-400']">
            {{ workflowMissing ? 'warning' : 'info' }}
          </BaseIcon>
          <div class="min-w-0">
            <div :class="['text-sm font-semibold leading-tight', workflowMissing ? 'text-amber-800' : 'text-slate-700']">
              {{ workflowMissing ? 'Workflow não configurado' : !canEdit ? 'Sem permissão' : !canStartParecer ? 'Parecer indisponível' : 'Sem parecer emitido' }}
            </div>
            <div class="mt-0.5 text-xs text-slate-500">
              <template v-if="workflowMissing">O administrador precisa configurar o workflow de Parecer para esta conta.</template>
              <template v-else-if="!canEdit">{{ permissionMessage }}</template>
              <template v-else-if="!canStartParecer">
                {{ startParecerBlockedReason }}
              </template>
              <template v-else>Parecer é opcional quando não existe relacionamento comercial.</template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!workflowMissing" class="mt-2 flex justify-end">
        <span v-tooltip.top="startParecerTooltip">
          <SplitButton
            label="Criar parecer"
            :model="canEdit ? parecerMenuItems : []"
            severity="secondary"
            outlined
            size="small"
            class="!h-8 !text-xs"
            :disabled="isStartParecerDisabled"
            @click="handleStartParecer"
          >
            <template #icon>
              <BaseIcon class="text-sm">edit_note</BaseIcon>
            </template>
          </SplitButton>
        </span>
      </div>
    </div>
  </section>
</template>
